/**
 * Firebase App Check (reCAPTCHA v3).
 *
 * Serve pra provar que a requisição veio do NOSSO app rodando num browser
 * de verdade — não de um script batendo direto na Cloud Function. O portal
 * é público (link divulgado por WhatsApp), então `/auth/signup` e
 * `/auth/magic-link` estão expostos a qualquer curl.
 *
 * ─── Contrato: chave ausente = App Check inexistente ───
 *
 * Se `VITE_APPCHECK_RECAPTCHA_KEY` não estiver definida, NADA acontece:
 * não inicializa e `appCheckHeader()` devolve `{}`. Isso é essencial e não
 * é preguiça — `initializeAppCheck` com site key inválida LANÇA, e este
 * módulo é carregado por `config.ts`, que é importado por dezenas de
 * arquivos incluindo a árvore do App.tsx. Uma exceção aqui seria tela
 * branca no sistema inteiro do consultório, staff junto.
 *
 * Pelo mesmo motivo, todo `getToken` fica em try/catch: o reCAPTCHA falha
 * em rede corporativa, com adblock agressivo, ou quando o google.com está
 * inacessível. Sem o catch, um bloqueador de anúncio derrubaria o portal.
 *
 * ─── Como ligar (Leopoldo, no console) ───
 * 1. Firebase Console → App Check → registrar o Web App com reCAPTCHA v3;
 *    cadastrar dratento.web.app, dratento.com e localhost.
 * 2. Copiar a SITE KEY (é pública, pode ir no bundle) pro `.env` como
 *    VITE_APPCHECK_RECAPTCHA_KEY, e refazer o build + deploy de hosting.
 * 3. Deixar tudo "Unenforced" no console e olhar as métricas por ~1 semana.
 * 4. Só então trocar APP_CHECK_MODE de 'audit' pra 'enforce' em
 *    functions/.env e redeployar a function.
 *
 * Nunca subir frontend e `enforce` no mesmo deploy: o service worker do
 * PWA mantém o bundle antigo (sem o header) rodando em abas abertas.
 */
import { initializeAppCheck, ReCaptchaV3Provider, getToken, type AppCheck } from 'firebase/app-check';
import type { FirebaseApp } from 'firebase/app';

const SITE_KEY = import.meta.env.VITE_APPCHECK_RECAPTCHA_KEY as string | undefined;

let appCheckInstance: AppCheck | null = null;

/** Inicializa App Check. No-op sem site key. Chamar ANTES de getFirestore/
 *  getStorage/getDatabase, senão o SDK cria os clientes sem o interceptor
 *  e não anexa token automaticamente nas chamadas de Firestore. */
export function initAppCheck(app: FirebaseApp): void {
  if (!SITE_KEY) return;
  try {
    // Em dev, um debug token registrado no console permite testar sem
    // resolver reCAPTCHA. Precisa ser setado ANTES de initializeAppCheck.
    if (import.meta.env.DEV && import.meta.env.VITE_APPCHECK_DEBUG_TOKEN) {
      (self as unknown as Record<string, unknown>).FIREBASE_APPCHECK_DEBUG_TOKEN =
        import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
    }
    appCheckInstance = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (err) {
    // Chave malformada, domínio não cadastrado, etc. Seguimos sem App Check.
    console.warn('[app-check] inicialização falhou; seguindo sem App Check:', err);
    appCheckInstance = null;
  }
}

/**
 * Header pra anexar em chamadas `fetch` manuais.
 *
 * O portal fala com a Cloud Function por fetch cru (não por httpsCallable),
 * então o token NÃO viaja sozinho — precisa ir neste header.
 * Devolve `{}` quando App Check está desligado ou o token falha.
 */
export async function appCheckHeader(): Promise<Record<string, string>> {
  if (!appCheckInstance) return {};
  try {
    const { token } = await getToken(appCheckInstance, /* forceRefresh */ false);
    return token ? { 'X-Firebase-AppCheck': token } : {};
  } catch {
    return {};
  }
}
